<template>
  <v-container fluid :aria-busy="loading ? 'true' : 'false'">
    <v-card class="elevation-2 mb-4">
      <v-toolbar color="primary" dark flat>
        <v-btn icon @click="back" title="Volver a colaboradores" aria-label="Volver a colaboradores">
          <v-icon aria-hidden="true">mdi-arrow-left</v-icon>
        </v-btn>
        <v-icon class="mr-2" aria-hidden="true">mdi-account-hard-hat</v-icon>
        <v-toolbar-title>Detalles del colaborador</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="load" :disabled="loading" title="Actualizar" aria-label="Actualizar datos">
          <v-icon aria-hidden="true">mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>
    </v-card>

    <template v-if="loading && !colaborador">
      <v-skeleton-loader type="list-item-avatar-two-line" class="elevation-1 mb-4"></v-skeleton-loader>
      <v-row>
        <v-col cols="12" md="4"><v-skeleton-loader type="card, list-item-two-line@3" class="elevation-1"></v-skeleton-loader></v-col>
        <v-col cols="12" md="8"><v-skeleton-loader type="table-heading, table-tbody" class="elevation-1"></v-skeleton-loader></v-col>
      </v-row>
    </template>

    <v-alert v-if="notFound" type="warning" text>
      No se encontró el colaborador en tu grupo.
      <template v-slot:append>
        <v-btn small text color="warning" @click="back">Volver al listado</v-btn>
      </template>
    </v-alert>

    <template v-if="colaborador">
      <v-card class="elevation-1 mb-4">
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar color="primary" size="56" class="mr-4"><v-icon dark size="30" aria-hidden="true">mdi-account-hard-hat</v-icon></v-avatar>
            <div class="min-width-0">
              <h1 class="text-h5 font-weight-medium text--primary colaborador-name">{{ colaborador.name }}</h1>
              <div class="mt-2 d-flex flex-wrap align-center">
                <v-chip small class="mr-1 mb-1" :color="colaborador.status === STATUS_ACTIVE ? 'success' : 'grey darken-1'" dark>
                  <v-icon left x-small aria-hidden="true">{{ colaborador.status === STATUS_ACTIVE ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                  {{ colaborador.status === STATUS_ACTIVE ? 'Activo' : 'Deshabilitado' }}
                </v-chip>
                <v-chip small class="mr-1 mb-1" color="primary" outlined>{{ colaboradorTypeLabel(colaborador.colaborador_type) }}</v-chip>
                <v-chip v-for="d in colaborador.dealers" :key="d.id" small class="mr-1 mb-1">{{ d.name }}</v-chip>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="12" md="4">
          <v-card class="elevation-1 fill-height">
            <v-card-title class="pb-1 text-subtitle-1 font-weight-medium">
              <v-icon left color="primary" aria-hidden="true">mdi-information</v-icon>
              Información
            </v-card-title>
            <v-divider></v-divider>
            <v-list dense class="py-2">
              <v-list-item v-for="field in fields" :key="field.label" class="detail-item">
                <v-list-item-icon class="mr-3"><v-icon color="primary" small aria-hidden="true">{{ field.icon }}</v-icon></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="caption grey--text text--darken-1">{{ field.label }}</v-list-item-title>
                  <v-list-item-subtitle class="text-body-2 text--primary detail-value">
                    <a v-if="field.value && field.href" :href="field.href" class="text-decoration-none">{{ field.value }}</a>
                    <template v-else>{{ field.value || '—' }}</template>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <v-card class="elevation-1">
            <v-tabs v-model="tab" show-arrows>
              <v-tab>
                <v-icon left small aria-hidden="true">mdi-map-marker</v-icon>
                Ubicaciones
                <v-chip x-small class="ml-2">{{ locations.length }}</v-chip>
              </v-tab>
              <v-tab>
                <v-icon left small aria-hidden="true">mdi-account-group</v-icon>
                Usuarios
                <v-chip x-small class="ml-2">{{ users.length }}</v-chip>
              </v-tab>
            </v-tabs>
            <v-divider></v-divider>

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <div class="table-wrap">
                  <v-data-table :headers="locationHeaders" :items="locations" item-key="id" :loading="loadingLocations"
                    :items-per-page="10" dense :footer-props="{ itemsPerPageOptions: [10, 25, 50] }">
                    <template v-slot:item.name="{ item }">
                      <div class="font-weight-medium">{{ item.name }}</div>
                      <div class="text-caption grey--text text--darken-1" v-if="item.type">{{ item.type }}</div>
                    </template>
                    <template v-slot:item.address="{ item }">{{ locationAddress(item) }}</template>
                    <template v-slot:item.phone="{ item }">
                      <a v-if="item.phone" :href="`tel:${item.phone}`" class="text-decoration-none">{{ item.phone }}</a>
                      <span v-else>—</span>
                    </template>
                    <template v-slot:no-data>
                      <div class="empty-state">
                        <v-icon size="40" color="grey lighten-1" aria-hidden="true">mdi-map-marker-off-outline</v-icon>
                        <div class="subtitle-2 mt-2">Este colaborador aún no tiene ubicaciones</div>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-tab-item>

              <v-tab-item>
                <div class="table-wrap">
                  <v-data-table :headers="userHeaders" :items="users" item-key="id" :loading="loadingUsers"
                    :items-per-page="10" dense :footer-props="{ itemsPerPageOptions: [10, 25, 50] }">
                    <template v-slot:item.name="{ item }">
                      <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
                      <a :href="`mailto:${item.email}`" class="text-caption text-decoration-none">{{ item.email }}</a>
                    </template>
                    <template v-slot:item.phone="{ item }">
                      <a v-if="item.phone" :href="`tel:${item.phone}`" class="text-decoration-none">{{ item.phone }}</a>
                      <span v-else>—</span>
                    </template>
                    <template v-slot:item.status="{ item }">
                      <v-chip x-small :color="item.status === STATUS_ACTIVE ? 'success' : 'grey darken-1'" dark>
                        <v-icon left x-small aria-hidden="true">{{ item.status === STATUS_ACTIVE ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                        {{ item.status === STATUS_ACTIVE ? 'Activo' : 'Inactivo' }}
                      </v-chip>
                    </template>
                    <template v-slot:no-data>
                      <div class="empty-state">
                        <v-icon size="40" color="grey lighten-1" aria-hidden="true">mdi-account-off-outline</v-icon>
                        <div class="subtitle-2 mt-2">Este colaborador aún no tiene usuarios</div>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import GrupoPanelService from '@/services/GrupoPanelService'
import { STATUS_ACTIVE, colaboradorTypeLabel, apiData, apiError } from '@/grupo'

// Entorno Grupo: ficha de un colaborador del grupo con sus ubicaciones y sus usuarios
export default {
  name: 'GrupoColaboradorDetail',

  props: {
    colaborador_id: { type: [String, Number], required: true }
  },

  data: () => ({
    STATUS_ACTIVE,
    colaborador: null,
    users: [],
    locations: [],
    tab: 0,
    loading: false,
    loadingUsers: false,
    loadingLocations: false,
    notFound: false,
    locationHeaders: [
      { text: 'Ubicación', value: 'name' },
      { text: 'Dirección', value: 'address', sortable: false },
      { text: 'Teléfono', value: 'phone' }
    ],
    userHeaders: [
      { text: 'Usuario', value: 'name' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Estado', value: 'status', align: 'center' }
    ]
  }),

  computed: {
    fields () {
      const c = this.colaborador
      return [
        { icon: 'mdi-card-account-details-outline', label: 'CIF', value: c.cif },
        { icon: 'mdi-phone', label: 'Teléfono', value: c.phone, href: c.phone ? `tel:${c.phone}` : null },
        { icon: 'mdi-email', label: 'Correo electrónico', value: c.email, href: c.email ? `mailto:${c.email}` : null }
      ]
    }
  },

  watch: {
    colaborador_id () {
      this.load()
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    colaboradorTypeLabel,

    back () {
      this.$router.push({ name: 'GrupoColaboradores' })
    },

    locationAddress (location) {
      const parts = [location.address, location.city, location.zip ? `(${location.zip})` : null].filter(Boolean)
      return parts.join(', ') || '—'
    },

    async load () {
      this.loading = true
      this.notFound = false
      try {
        // Si el colaborador no está vinculado al grupo no aparece en el listado
        const colaboradores = apiData(await GrupoPanelService.colaboradores()) || []
        this.colaborador = colaboradores.find(c => String(c.id) === String(this.colaborador_id)) || null
        if (!this.colaborador) {
          this.notFound = true
          this.users = []
          this.locations = []
          return
        }
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudo cargar el colaborador'))
        return
      } finally {
        this.loading = false
      }
      this.loadUsers()
      this.loadLocations()
    },

    async loadUsers () {
      this.loadingUsers = true
      try {
        this.users = apiData(await GrupoPanelService.colaboradorUsers(this.colaborador.id)) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar los usuarios'))
      } finally {
        this.loadingUsers = false
      }
    },

    async loadLocations () {
      this.loadingLocations = true
      try {
        this.locations = apiData(await GrupoPanelService.colaboradorLocations(this.colaborador.id)) || []
      } catch (error) {
        this.$toast.error(apiError(error, 'No se pudieron cargar las ubicaciones'))
      } finally {
        this.loadingLocations = false
      }
    }
  }
}
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.colaborador-name {
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.detail-item {
  min-height: 52px;
}

.detail-value {
  white-space: normal;
  overflow-wrap: anywhere;
}

.table-wrap {
  min-height: 300px;
  overflow-x: auto;
}

.empty-state {
  text-align: center;
  padding: 32px 16px;
}
</style>
